import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import { ArrowLeft, TrendingDown, FileText, Calendar, Car, Gauge, CheckCircle2, CreditCard } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { API } from '@/lib/api';
import { httpRequest } from '@/lib/http';
import ContractDigestCard from '@/components/ContractDigestCard';
import EconomyPossibleDialog, { economyCongratsEligible } from '@/components/EconomyPossibleDialog';

export default function AnalysisDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [economyDialogOpen, setEconomyDialogOpen] = useState(false);
  const economyCongratsSigRef = useRef('');

  const formatCurrency = useCallback((val) => {
    if (val == null) return '0 EUR';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(val);
  }, []);

  useEffect(() => {
    economyCongratsSigRef.current = '';
  }, [id]);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await httpRequest(`${API}/analyses/${id}`);
        setAnalysis(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  useEffect(() => {
    if (!analysis?.result || !economyCongratsEligible(analysis.result)) return;
    const total = analysis.result.savings_analysis?.total_savings;
    const rem = analysis.result.contract_details?.remaining_months;
    const rc = analysis.reanalyze_count ?? 0;
    const sig = `${id}|${total}|${rem}|${rc}`;
    if (sig === economyCongratsSigRef.current) return;
    economyCongratsSigRef.current = sig;
    setEconomyDialogOpen(true);
  }, [id, analysis]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <div className="pt-24 flex items-center justify-center">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <div className="pt-24 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-[#f5f5f7]/50">Analyse non trouvee</p>
          <button onClick={() => navigate('/dashboard')} className="mt-4 text-green-400 font-semibold text-sm">
            Retour au dashboard
          </button>
        </div>
      </div>
    );
  }

  const r = analysis.result || {};
  const savings = r.savings_analysis || {};
  const contract = r.contract_details || {};
  const vehicle = r.vehicle || {};
  const commission = r.commission || {};
  const payStatus = analysis.payment_status || 'none';
  const awaitingCommission =
    analysis.has_savings &&
    !['accepted', 'in_progress', 'completed', 'cancelled'].includes(payStatus) &&
    (payStatus === 'pending' || payStatus === 'none');

  return (
    <div className="min-h-screen bg-black" data-testid="analysis-detail-page">
      <Header />
      <main className="pt-24 pb-16 max-w-5xl mx-auto px-6 md:px-12">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          data-testid="back-btn"
          className="flex items-center gap-2 text-sm text-[#f5f5f7]/50 hover:text-[#f5f5f7] transition-[opacity,transform] duration-[180ms] mb-8"
        >
          <ArrowLeft size={16} />
          Retour
        </button>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-10">
          <div>
            <h1 className="text-3xl sm:text-4xl tracking-tight font-bold text-[#f5f5f7] font-heading">
              Analyse detaillee
            </h1>
            <p className="text-sm text-[#f5f5f7]/50 mt-1">
              {analysis.document?.original_filename || 'Document'}
              <Badge variant="outline" className="ml-2 rounded-lg border-white/8 text-[#f5f5f7]/60">{r.document_type || 'N/A'}</Badge>
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2">
              <CheckCircle2 size={18} className="text-green-400" />
              <span className="text-sm font-medium text-green-400">Analyse terminee</span>
            </div>
            {awaitingCommission ? (
              <Badge className="rounded-lg bg-orange-500/10 text-orange-400 border-orange-500/30">A confirmer — commission</Badge>
            ) : null}
          </div>
        </div>

        {awaitingCommission ? (
          <div
            className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border border-orange-500/30 bg-orange-500/10 p-5 rounded-xl"
            data-testid="analysis-pay-cta"
          >
            <div className="flex gap-3">
              <CreditCard className="h-6 w-6 shrink-0 text-orange-400" />
              <div>
                <p className="font-semibold text-orange-400">Finaliser votre demande</p>
                <p className="text-sm text-orange-400/90 mt-1">
                  Choisissez votre formule (Essentiel ou Sérénité), puis le paiement ou la confirmation des frais se fait depuis le
                  tableau de bord : ouvrez la même analyse et cliquez sur <strong>Confirmer et payer</strong>.
                </p>
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              className="rounded-lg border-orange-500/30 bg-transparent text-orange-400 hover:bg-orange-500/15 shrink-0"
              onClick={() => navigate('/dashboard')}
            >
              Aller au tableau de bord
            </Button>
          </div>
        ) : null}

        {/* Big Savings Card */}
        <div className="bg-[#1c1c1e] border border-white/8 p-8 mb-8 rounded-xl" data-testid="savings-hero">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
            <div>
              <p className="text-sm uppercase tracking-[0.15em] font-semibold text-[#f5f5f7]/40 mb-2">Mensualite actuelle</p>
              <p className="text-4xl font-black text-[#f5f5f7] font-heading">
                {formatCurrency(savings.current_monthly)}
              </p>
            </div>
            <div className="flex flex-col items-center justify-center">
              <TrendingDown size={32} className="text-green-400 mb-2" />
              <p className="text-sm font-bold text-green-400">
                -{savings.savings_percentage != null ? `${savings.savings_percentage}%` : 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-sm uppercase tracking-[0.15em] font-semibold text-green-400 mb-2">Mensualite proposee</p>
              <p className="text-4xl font-black savings-amount">
                {formatCurrency(savings.proposed_monthly)}
              </p>
            </div>
          </div>
        </div>

        {/* Commission Breakdown */}
        <div className="bg-[#1c1c1e] border border-white/8 p-8 mb-8 rounded-xl" data-testid="commission-detail">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#f5f5f7]/40 mb-6">Detail financier</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            <div>
              <p className="text-xs text-[#f5f5f7]/40 mb-1">Economie mensuelle</p>
              <p className="text-2xl font-black text-green-400 font-heading">
                {formatCurrency(savings.monthly_savings)}
              </p>
            </div>
            <div>
              <p className="text-xs text-[#f5f5f7]/40 mb-1">Mois restants</p>
              <p className="text-2xl font-black text-[#f5f5f7]/80 font-heading">
                {contract.remaining_months ?? 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-xs text-[#f5f5f7]/40 mb-1">Economie totale (restant)</p>
              <p className="text-2xl font-black text-green-400 font-heading">
                {formatCurrency(savings.total_savings)}
              </p>
            </div>
            <div>
              <p className="text-xs text-[#f5f5f7]/40 mb-1">Frais (formule au tableau de bord)</p>
              {r.plan_pricing?.essentiel?.amount != null && r.plan_pricing?.serenite?.amount != null ? (
                <div className="space-y-1">
                  <p className="text-lg font-black font-heading commission-amount">
                    Essentiel {formatCurrency(r.plan_pricing.essentiel.amount)}
                  </p>
                  <p className="text-lg font-black font-heading text-blue-400/90">
                    Sérénité {formatCurrency(r.plan_pricing.serenite.amount)}
                  </p>
                </div>
              ) : (
                <p className="text-2xl font-black font-heading commission-amount">
                  {formatCurrency(commission.amount)}
                </p>
              )}
            </div>
            <div>
              <p className="text-xs text-[#f5f5f7]/40 mb-1">Votre economie nette</p>
              <p className="text-2xl font-black text-green-400 font-heading">
                {formatCurrency(commission.net_savings_for_client)}
              </p>
            </div>
          </div>
        </div>

        {id ? (
          <ContractDigestCard
            markdown={r.markdown_report || ''}
            reportHref={`${API}/analyses/${id}/report.md`}
            tone="neutral"
            className="mb-8"
          />
        ) : null}

        {/* Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Vehicle */}
          <div className="bg-[#1c1c1e] border border-white/8 p-6 rounded-xl" data-testid="vehicle-details">
            <div className="flex items-center gap-2 mb-4">
              <Car size={18} className="text-[#f5f5f7]/40" />
              <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#f5f5f7]/40">Vehicule</p>
            </div>
            <p className="text-xl font-bold text-[#f5f5f7] mb-2 font-heading">
              {vehicle.make || 'N/A'} {vehicle.model || ''}
            </p>
            <div className="space-y-1 text-sm text-[#f5f5f7]/60">
              {vehicle.year && <p>Annee: {vehicle.year}</p>}
              <p>Type de contrat: <span className="font-medium text-[#f5f5f7]/80">{r.document_type || 'N/A'}</span></p>
            </div>
          </div>

          {/* Contract */}
          <div className="bg-[#1c1c1e] border border-white/8 p-6 rounded-xl" data-testid="contract-details">
            <div className="flex items-center gap-2 mb-4">
              <FileText size={18} className="text-[#f5f5f7]/40" />
              <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#f5f5f7]/40">Contrat</p>
            </div>
            <div className="space-y-2 text-sm text-[#f5f5f7]/60">
              <div className="flex justify-between">
                <span>Duree totale</span>
                <span className="font-medium text-[#f5f5f7]/80">{contract.duration_months || 'N/A'} mois</span>
              </div>
              <div className="flex justify-between">
                <span>Mois restants</span>
                <span className="font-medium text-[#f5f5f7]/80">{contract.remaining_months || 'N/A'} mois</span>
              </div>
              <div className="flex justify-between">
                <span>Kilometrage</span>
                <span className="font-medium text-[#f5f5f7]/80">{contract.total_mileage ? contract.total_mileage.toLocaleString('fr-FR') + ' km' : 'N/A'}</span>
              </div>
              {contract.start_date && (
                <div className="flex justify-between">
                  <span>Debut</span>
                  <span className="font-medium text-[#f5f5f7]/80">{contract.start_date}</span>
                </div>
              )}
              {contract.end_date && (
                <div className="flex justify-between">
                  <span>Fin</span>
                  <span className="font-medium text-[#f5f5f7]/80">{contract.end_date}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recommendations */}
        {r.recommendations?.length > 0 && (
          <div className="bg-[#1c1c1e] border border-white/8 p-6 mb-8 rounded-xl" data-testid="recommendations">
            <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#f5f5f7]/40 mb-4">Recommandations</p>
            <div className="space-y-3">
              {r.recommendations.map((rec, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <div className="w-6 h-6 bg-green-600 flex items-center justify-center flex-shrink-0 rounded">
                    <span className="text-[#f5f5f7] text-xs font-bold">{i + 1}</span>
                  </div>
                  <p className="text-sm text-[#f5f5f7]/80">{rec}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Summary */}
        {r.summary && (
          <div className="bg-[#1c1c1e] border border-white/8 p-6 rounded-xl" data-testid="analysis-summary">
            <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#f5f5f7]/40 mb-3">Resume</p>
            <p className="text-base leading-relaxed text-[#f5f5f7]/60">{r.summary}</p>
            {r.confidence_score != null && (
              <div className="mt-4 flex items-center gap-2">
                <Gauge size={14} className="text-[#f5f5f7]/40" />
                <p className="text-xs text-[#f5f5f7]/40">Confiance: {Math.round(r.confidence_score * 100)}%</p>
              </div>
            )}
          </div>
        )}
      </main>

      <EconomyPossibleDialog
        open={economyDialogOpen}
        onOpenChange={setEconomyDialogOpen}
        result={r}
        formatCurrency={formatCurrency}
      />
    </div>
  );
}
